/**
 * Alert History パネル（テーブル）
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { type AlertItem, getAlerts } from "../lib/api.ts";

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const PAGE_SIZE = 10;

export default function AlertsPanel({ mac }: { mac: string }) {
  const { t } = useTranslation();
  const [alerts, setAlerts] = useState<AlertItem[] | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await getAlerts(mac, PAGE_SIZE, (page - 1) * PAGE_SIZE);
        if (!cancelled) {
          setAlerts(res.alerts);
          setTotal(res.total);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mac, page]);

  if (error) return <p className="nx-error">{error}</p>;
  if (alerts === null) return <p>{t("common.loading")}</p>;
  if (alerts.length === 0) return null;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <section className="nx-alerts">
      <div className="nx-alerts-head">
        <p className="nx-label">{t("device_detail.alerts")}</p>
        <span className="nx-text-secondary">
          {t("alerts.summary", { current: alerts.length, total })}
        </span>
      </div>
      <table className="nx-alerts-table">
        <thead>
          <tr>
            <th>{t("alerts.ts")}</th>
            <th>{t("alerts.type")}</th>
            <th>{t("alerts.message")}</th>
            <th>{t("alerts.resolved")}</th>
          </tr>
        </thead>
        <tbody>
          {alerts.map((a) => (
            <tr key={a.id}>
              <td className="nx-mono nx-text-secondary">{fmtDate(a.timestamp)}</td>
              <td>
                <span className={`nx-badge ${a.resolved ? "is-ok" : "is-bad"}`}>{a.type}</span>
              </td>
              <td>{a.message}</td>
              <td>
                <span className={a.resolved ? "nx-text-secondary" : "nx-error"}>
                  {a.resolved ? t("alerts.resolved_yes") : t("alerts.resolved_no")}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {total > PAGE_SIZE && (
        <div className="nx-pager">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="nx-btn-outline"
          >
            ← {t("common.prev")}
          </button>
          <span className="nx-text-secondary">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="nx-btn-outline"
          >
            {t("common.next")} →
          </button>
        </div>
      )}
    </section>
  );
}
